import { useState, useEffect, useRef } from "react";
import {
  X,
  Play,
  CheckCircle2,
  XCircle,
  Loader2,
  BookOpen,
  Video,
  AlertCircle,
  ArrowRight,
  RotateCcw,
  Sparkles,
  Minimize2,
  Maximize2,
  Lightbulb,
  Eye,
  Target,
  Bot,
  UserRound,
  MessagesSquare,
} from "lucide-react";
import { ApiService } from "../../services/api";
import { useTheme } from "../../contexts/ThemeContext";

interface TutorQuestion {
  id: number;
  text: string;
  options: {
    A: string;
    B: string;
    C: string;
    D: string;
  };
  correctAnswer: string;
  mediaUrl?: string;
  solutionVideoUrl?: string;
}

interface TutorSession {
  gainId: string;
  gainText: string;
  lesson: { id: string; title: string } | null;
  topic: { id: string; title: string } | null;
  content: { id: string; title: string; type: string } | null;
  video: {
    id: string;
    title: string;
    url: string;
    startTime: string;
    endTime: string;
  } | null;
  explanation: string;
  examples: string[];
  lessonFlow?: string[];
  teacherDialogue?: Array<{
    speaker: "teacher" | "student";
    type?: string;
    text: string;
  }>;
  transcriptHighlights?: Array<{
    startTime?: string;
    endTime?: string;
    text: string;
  }>;
  questions: TutorQuestion[];
}

interface GainItem {
  id: string;
  gainText?: string;
  lessonSectionContentGain?: {
    gain?: string;
  };
  lesson?: {
    id: string;
    title: string;
  };
  topic?: {
    id: string;
    title: string;
  };
}

interface DeficiencyTutorPanelProps {
  isOpen: boolean;
  onClose: () => void;
  gainId: string;
  allGains?: GainItem[];
  onGainResolved?: () => void;
  sidebarExpanded?: boolean;

  mode?: "single" | "topic";
  topicData?: {
    contentTitle: string;
    gains: Array<{ id: string; gainText: string; startTime: string; endTime: string; isResolved?: boolean }>;
    gainIds: string[];
    videos: Array<{ id: string; title: string; url: string; startTime: string; endTime: string }>;
    lesson: { id: string; title: string } | null;
    topic: { id: string; title: string } | null;
  };
}

type TutorStep = "selection" | "loading" | "intro" | "sourceSelection" | "video" | "explanation" | "dialogue" | "quiz" | "result" | "topicIntro" | "reviewQuestion" | "reviewVideo";
type QuestionSource = "ai" | "questionPool";

const DeficiencyTutorPanel = ({
  isOpen,
  onClose,
  gainId,
  allGains = [],
  onGainResolved,
  sidebarExpanded = true,
  mode = "single",
  topicData
}: DeficiencyTutorPanelProps) => {
  const [step, setStep] = useState<TutorStep>("loading");
  const [session, setSession] = useState<TutorSession | null>(null);
  const [gainInfo, setGainInfo] = useState<{
    gainText: string;
    video: { id: string; title: string; url: string; startTime: string; endTime: string } | null;
    questionPoolCount: number;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedAnswers, setSelectedAnswers] = useState<Record<number, string>>({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [quizResult, setQuizResult] = useState<{
    results: Array<{ questionId: number; userAnswer: string; correctAnswer: string; isCorrect: boolean }>;
    correctCount: number;
    totalQuestions: number;
    allCorrect: boolean;
    message: string;
  } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isSessionLoading, setIsSessionLoading] = useState(false);
  const [currentGainId, setCurrentGainId] = useState<string>(gainId);
  const [completedGainIds, setCompletedGainIds] = useState<string[]>([]);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);
  const [selectedSource, setSelectedSource] = useState<QuestionSource>("ai");
  const selectedSourceRef = useRef<QuestionSource>("ai");
  const scrollRef = useRef<HTMLDivElement>(null);
  const { currentTheme } = useTheme();

  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [topicQuestionPoolCount, setTopicQuestionPoolCount] = useState(0);
  const [currentReviewIndex, setCurrentReviewIndex] = useState(0);
  const [wrongQuestions, setWrongQuestions] = useState<Array<{ question: TutorQuestion; result: { questionId: number; userAnswer: string; correctAnswer: string; isCorrect: boolean }; index: number }>>([]);
  const [visibleDialogueCount, setVisibleDialogueCount] = useState(0);

  const remainingGains = (allGains || []).filter(g => !completedGainIds.includes(g.id) && g.id !== currentGainId);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    if (isOpen) {
      setIsExpanded(true); // Her zaman tam ekran aç
      setCurrentGainId(gainId);
      setCompletedGainIds([]);
      setCurrentVideoIndex(0);

      if (mode === "topic" && topicData?.contentTitle) {
        loadTopicInfo();
      } else if (allGains && allGains.length > 1) {
        setStep("selection");
      } else if (gainId) {
        loadGainInfo(gainId);
      }
    }
}, [isOpen, gainId, mode, topicData?.contentTitle]);

  useEffect(() => {
    if (!isOpen) {
      setStep("loading");
      setSession(null);
      setGainInfo(null);
      setError(null);
      setSelectedAnswers({});
      setCurrentQuestionIndex(0);
      setCurrentVideoIndex(0);
      setQuizResult(null);
      setIsSessionLoading(false);
      setIsExpanded(false);
      setTopicQuestionPoolCount(0);
      setCurrentReviewIndex(0);
      setWrongQuestions([]);
      setVisibleDialogueCount(0);
    }
  }, [isOpen]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [step]);

  useEffect(() => {
    if (step !== "dialogue") {
      setVisibleDialogueCount(0);
      return;
    }

    const dialogueLength = session?.teacherDialogue?.filter((message) => message.text?.trim()).length || 0;
    setVisibleDialogueCount(dialogueLength > 0 ? 1 : 0);
  }, [step, session?.teacherDialogue]);

  useEffect(() => {
    const dialogueLength = session?.teacherDialogue?.filter((message) => message.text?.trim()).length || 0;
    if (step !== "dialogue" || visibleDialogueCount >= dialogueLength) return;

    const timeout = window.setTimeout(() => {
      setVisibleDialogueCount((count) => Math.min(count + 1, dialogueLength));
    }, 950);

    return () => window.clearTimeout(timeout);
  }, [step, visibleDialogueCount, session?.teacherDialogue]);

  useEffect(() => {
    if (step === "dialogue" && scrollRef.current) {
      scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    }
  }, [step, visibleDialogueCount]);

  const loadGainInfo = async (targetGainId: string) => {
    setStep("loading");
    setError(null);
    setSession(null);
    setGainInfo(null);
    setSelectedAnswers({});
    setCurrentQuestionIndex(0);
    setQuizResult(null);
    try {
      const response = await ApiService.getTutorGainDetails(targetGainId);
      const questionPoolCount = response.questionPoolCount || 0;

      setGainInfo({
        gainText: response.gainText,
        video: response.video ? {
          id: response.video.id,
          title: response.video.title,
          url: response.video.url,
          startTime: response.startTime,
          endTime: response.endTime,
        } : null,
        questionPoolCount,
      });

      // Her zaman soru havuzunu kullan
      setSelectedSource("questionPool");
      selectedSourceRef.current = "questionPool";
      setStep("intro");
    } catch (err: any) {
      setError(err.message || "Kazanım bilgisi alınamadı");
      setStep("loading");
    }
  };

  const loadTopicInfo = async () => {
    if (!topicData) return;
    setError(null);
    setSession(null);
    setSelectedAnswers({});
    setCurrentQuestionIndex(0);
    setCurrentVideoIndex(0);
    setQuizResult(null);

    setIsExpanded(true);
    setStep("topicIntro");
  };

  const startTopicSession = async () => {
    if (!topicData) return;
    setIsSessionLoading(true);
    setError(null);

    try {
      const response = await ApiService.startTopicTutorSession(topicData.contentTitle);
      setSession({
        gainId: "",
        gainText: topicData.contentTitle,
        lesson: topicData.lesson,
        topic: topicData.topic,
        content: { id: "", title: topicData.contentTitle, type: "topic" },
        video: null,
        explanation: response.explanation || "",
        examples: response.examples || [],
        teacherDialogue: response.teacherDialogue || [],
        lessonFlow: response.lessonFlow || [],
        transcriptHighlights: response.transcriptHighlights || [],
        questions: response.questions || [],
      });

      if (topicData.videos.length > 0) {
        setIsExpanded(true);
        setStep("video");
      } else {
        setStep("explanation");
      }
    } catch (err: any) {
      setError(err.message || "Oturum başlatılamadı");
      setStep("loading");
    } finally {
      setIsSessionLoading(false);
    }
  };

  const handleNextVideo = () => {
    if (!topicData) return;
    if (currentVideoIndex < topicData.videos.length - 1) {
      setCurrentVideoIndex(prev => prev + 1);
    } else {
      setStep("explanation");
    }
  };

  const handlePrevVideo = () => {
    if (currentVideoIndex > 0) {
      setCurrentVideoIndex(prev => prev - 1);
    }
  };

  const handleSkipTopicVideos = () => {
    setStep("explanation");
  };

  const handleSubmitTopicQuiz = async () => {
    if (!session || !topicData) return;

    const answers = session.questions.map((q) => ({
      questionId: q.id,
      userAnswer: selectedAnswers[q.id] || "",
      correctAnswer: q.correctAnswer,
    }));

    setIsSubmitting(true);

    try {
      const result = await ApiService.checkTopicTutorAnswers(topicData.gainIds, answers);
      setQuizResult(result);
      setStep("result");

      if (result.allCorrect && onGainResolved) {
        onGainResolved();
      }
    } catch (err: any) {
      setError(err.message || "Cevaplar kontrol edilemedi");
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectGain = (targetGainId: string) => {
    setCurrentGainId(targetGainId);
    loadGainInfo(targetGainId);
  };

  const loadSessionInBackground = async (source: QuestionSource = selectedSourceRef.current) => {
    if (session || isSessionLoading) return;
    setIsSessionLoading(true);
    try {
      const response = await ApiService.startTutorSession(currentGainId, source);
      setSession(response);
    } catch (err: any) {
      setError(err.message || "Oturum başlatılamadı");
    } finally {
      setIsSessionLoading(false);
    }
  };

  const startFullSession = async (source: QuestionSource = selectedSourceRef.current) => {
    setIsSessionLoading(true);
    setError(null);
    try {
      const response = await ApiService.startTutorSession(currentGainId, source);
      setSession(response);
      setStep("explanation");
    } catch (err: any) {
      setError(err.message || "Oturum başlatılamadı");
    } finally {
      setIsSessionLoading(false);
    }
  };

  const handleSourceSelect = (source: QuestionSource) => {
    setSelectedSource(source);
    selectedSourceRef.current = source;
    setStep("intro");
  };

  const parseTimeToSeconds = (timeStr: string): number => {
    if (!timeStr) return 0;
    const parts = timeStr.split(":");
    if (parts.length === 2) return parseInt(parts[0]) * 60 + parseInt(parts[1]);
    if (parts.length === 3) return parseInt(parts[0]) * 3600 + parseInt(parts[1]) * 60 + parseInt(parts[2]);
    return 0;
  };

  const getVimeoEmbedUrl = (url: string, startTime: string): string => {
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) {
      const videoId = vimeoMatch[1];
      const startSeconds = parseTimeToSeconds(startTime);
      return `https://player.vimeo.com/video/${videoId}?autoplay=1#t=${startSeconds}s`;
    }
    return url;
  };

  const getVimeoThumbnailUrl = (url: string): string | null => {
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) {
      const videoId = vimeoMatch[1];
      return `https://vumbnail.com/${videoId}.jpg`;
    }
    return null;
  };

  const handleWatchVideo = () => {
    setIsExpanded(true);
    setStep("video");
    loadSessionInBackground();
  };

  const handleSkipVideo = () => {
    if (session) {
      setStep("explanation");
    } else {
      startFullSession();
    }
  };

  const handleVideoComplete = () => {
    if (session) {
      setStep("explanation");
    } else {
      setStep("explanation");
    }
  };
  const handleStartQuiz = () => {
    setIsExpanded(true);
    setStep("quiz");
    setCurrentQuestionIndex(0);
    setSelectedAnswers({});
  };

  const handleContinueFromExplanation = () => {
    if (teacherDialogue.length > 0) {
      setIsExpanded(true);
      setStep("dialogue");
      return;
    }

    handleStartQuiz();
  };

  const handleAnswerSelect = (questionId: number, answer: string) => {
    setSelectedAnswers((prev) => ({ ...prev, [questionId]: answer }));
  };

  const handleNextQuestion = () => {
    if (session && currentQuestionIndex < session.questions.length - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
    }
  };

  const handlePrevQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((prev) => prev - 1);
    }
  };

  const handleSubmitQuiz = async () => {
    if (!session) return;

    if (mode === "topic" && topicData) {
      await handleSubmitTopicQuiz();
      return;
    }

    const answers = session.questions.map((q) => ({
      questionId: q.id,
      userAnswer: selectedAnswers[q.id] || "",
      correctAnswer: q.correctAnswer,
    }));
    setIsSubmitting(true);
    try {
      const result = await ApiService.checkTutorAnswers(currentGainId, answers);
      setQuizResult(result);
      setStep("result");
      if (result.allCorrect) {
        setCompletedGainIds(prev => [...prev, currentGainId]);
        if (onGainResolved) {
          onGainResolved();
        }
      }
    } catch (err: any) {
      setError(err.message || "Cevaplar kontrol edilemedi");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRetry = () => {
    if (mode === "topic" && topicData) {
      setStep("intro");
      setSession(null);
      setSelectedAnswers({});
      setCurrentQuestionIndex(0);
      setQuizResult(null);
      setCurrentReviewIndex(0);
      setWrongQuestions([]);
    } else {
      loadGainInfo(currentGainId);
    }
  };

  const startReview = () => {
    if (!quizResult || !session) return;
    
    const wrongs = session.questions
      .map((question, index) => {
        const result = quizResult.results.find((r) => r.questionId === question.id);
        if (result && !result.isCorrect) {
          return { question, result, index };
        }
        return null;
      })
      .filter(Boolean) as Array<{ question: TutorQuestion; result: { questionId: number; userAnswer: string; correctAnswer: string; isCorrect: boolean }; index: number }>;
    
    setWrongQuestions(wrongs);
    setCurrentReviewIndex(0);
    setIsExpanded(true);
    setStep("reviewQuestion");
  };

  const handleNextReviewStep = () => {
    const currentWrong = wrongQuestions[currentReviewIndex];
    
    if (step === "reviewQuestion") {
      if (currentWrong?.question.solutionVideoUrl) {
        setStep("reviewVideo");
      } else {
        if (currentReviewIndex < wrongQuestions.length - 1) {
          setCurrentReviewIndex(prev => prev + 1);
          setStep("reviewQuestion");
        } else {
          setStep("result");
        }
      }
    } else if (step === "reviewVideo") {
      if (currentReviewIndex < wrongQuestions.length - 1) {
        setCurrentReviewIndex(prev => prev + 1);
        setStep("reviewQuestion");
      } else {
        setStep("result");
      }
    }
  };

  const handlePrevReviewStep = () => {
    if (step === "reviewVideo") {
      setStep("reviewQuestion");
    } else if (step === "reviewQuestion" && currentReviewIndex > 0) {
      const prevWrong = wrongQuestions[currentReviewIndex - 1];
      setCurrentReviewIndex(prev => prev - 1);
      if (prevWrong?.question.solutionVideoUrl) {
        setStep("reviewVideo");
      } else {
        setStep("reviewQuestion");
      }
    } else {
      setStep("result");
    }
  };

  if (!isOpen) return null;

  const currentQuestion = session?.questions[currentQuestionIndex];
  const allAnswered = session?.questions.every((q) => selectedAnswers[q.id]) || false;
  const teacherDialogue = session?.teacherDialogue?.filter((message) => message.text?.trim()) || [];
  const lessonFlow = session?.lessonFlow?.filter(Boolean) || [];
  const transcriptHighlights = session?.transcriptHighlights?.filter((item) => item.text?.trim()) || [];

  return (
    <>
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@600;700&display=swap');

          .tutor-container::-webkit-scrollbar {
            display: none;
          }
          .tutor-container {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          .logo-text {
            font-family: 'Poppins', sans-serif;
            font-weight: 700;
            letter-spacing: -0.02em;
          }
          @keyframes tutorMessageIn {
            from {
              opacity: 0;
              transform: translateY(10px) scale(0.98);
            }
            to {
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }
          .tutor-message-in {
            animation: tutorMessageIn 280ms ease-out both;
          }
        `}
      </style>

      {isMobile && <div className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden" onClick={onClose} />}

      <div
        className={`fixed z-50 transition-all duration-500 ease-in-out ${
          isMobile
            ? "inset-0 top-[60px]"
            : isExpanded
            ? `top-[60px] right-0 bottom-0 ${sidebarExpanded ? "md:left-64" : "md:left-[50px]"}`
            : "top-[60px] right-0 bottom-0 w-80"
        }`}
      >
        <div
          className={`flex flex-col h-full shadow-2xl w-full transition-all duration-500 ease-in-out bg-gray-50 ${
            isMobile ? "rounded-none p-0" : "border border-gray-200 p-1"
          }`}
        >
          <div className="flex-shrink-0 relative px-6 py-4 sm:py-6 transition-all duration-500 ease-in-out">
            <div className="relative flex items-center justify-end">
              <div className={`absolute transition-all duration-500 ease-in-out ${isExpanded ? "left-1/2 -translate-x-1/2" : "left-0"}`}>
                <h2
                  className="logo-text text-2xl sm:text-3xl"
                  style={{
                    background: `linear-gradient(to right, ${currentTheme.colors.primary}, ${currentTheme.colors.secondary})`,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  Deep
                </h2>
              </div>

              <div className="flex items-center space-x-2 relative z-10">
                {!isMobile && (
                  <button
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="p-1.5 rounded transition-colors text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                    aria-label={isExpanded ? "Küçült" : "Genişlet"}
                    title={isExpanded ? "Küçült" : "Genişlet"}
                  >
                    {isExpanded ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                  </button>
                )}
                <button
                  onClick={onClose}
                  className="p-1.5 rounded transition-colors text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                  aria-label="Kapat"
                >
                  <X size={16} />
                </button>
              </div>
            </div>
          </div>

          <div
            ref={scrollRef}
            className={`tutor-container flex-1 overflow-y-auto px-3 sm:px-4 py-1 pb-4 transition-all duration-500 ease-in-out ${
              isExpanded
                ? step === "video"
                  ? "w-full px-6"
                  : step === "intro" || step === "selection"
                  ? "w-full px-6"
                  : "flex flex-col items-center w-full px-6"
                : "space-y-3 sm:space-y-4"
            }`}
            style={{ scrollbarWidth: "none", msOverflowStyle: "none", WebkitOverflowScrolling: "touch" }}
          >
            {step === "selection" && allGains.length > 1 && (
              <div className={`space-y-4 ${isExpanded ? "max-w-3xl w-full mx-auto" : ""}`}>
                <div
                  className={`rounded-2xl text-center shadow-lg border border-gray-200 ${isExpanded ? "p-8" : "p-5"}`}
                  style={{
                    background: `linear-gradient(135deg, ${currentTheme.colors.primary}15, ${currentTheme.colors.secondary}10, ${currentTheme.colors.accent}15)`,
                    borderColor: `${currentTheme.colors.primary}30`,
                  }}
                >
                  <h3
                    className={`font-semibold mb-3 ${isExpanded ? "text-2xl mb-4" : "text-lg mb-2"}`}
                    style={{
                      background: `linear-gradient(to right, ${currentTheme.colors.primary}, ${currentTheme.colors.accent})`,
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                    }}
                  >
                    Merhaba! 👋
                  </h3>
                  <p className={`text-gray-700 leading-relaxed ${isExpanded ? "text-base" : "text-sm"}`}>
                    <span className="font-semibold" style={{ color: currentTheme.colors.primary }}>{allGains[0]?.lesson?.title}</span> dersinin <span className="font-semibold" style={{ color: currentTheme.colors.primary }}>{allGains[0]?.topic?.title}</span> bölümündeki testleri tamamladığın için buradasın.
                    Tespit ettiğim eksiklerini gidermek için sana yardımcı olmaya hazırım. Hangi konuyla başlamak istersin?
                  </p>
                </div>

                <div className={`${isExpanded ? "space-y-3" : "space-y-2"}`}>
                  {allGains.filter(gain => !completedGainIds.includes(gain.id)).length > 0 ? (
                    allGains
                      .filter(gain => !completedGainIds.includes(gain.id))
                      .map((gain, index) => (
                        <button
                          key={gain.id}
                          onClick={() => selectGain(gain.id)}
                          className={`w-full rounded-xl border-2 text-left transition-all flex items-center gap-3 hover:border-gray-300 hover:bg-gray-50 border-gray-200 bg-white ${isExpanded ? "p-5" : "p-4"}`}
                        >
                          <span
                            className={`rounded-full flex items-center justify-center font-bold flex-shrink-0 text-white ${isExpanded ? "w-9 h-9 text-base" : "w-7 h-7 text-sm"}`}
                            style={{ backgroundColor: currentTheme.colors.primary }}
                          >
                            {index + 1}
                          </span>
                          <span className={`text-gray-700 leading-snug ${isExpanded ? "text-base" : "text-sm"}`}>{gain.gainText || gain.lessonSectionContentGain?.gain || `Kazanım ${index + 1}`}</span>
                        </button>
                      ))
                  ) : (
                    <div className="text-center p-8 bg-green-50 rounded-2xl border border-green-100 flex flex-col items-center">
                      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                        <Sparkles className="w-8 h-8 text-green-600" />
                      </div>
                      <h4 className="text-xl font-bold text-green-800 mb-2">Harikasın! 🎉</h4>
                      <p className="text-green-700 mb-6">Bu konudaki tüm eksikleri başarıyla tamamladın.</p>
                      <button
                        onClick={onClose}
                        className="px-6 py-2 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 transition-colors"
                      >
                        Tamamla ve Çık
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
            {step === "loading" && (
              <div className="flex flex-col items-center justify-center py-12 px-4">
                {error ? (
                  <>
                    <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
                    <p className="text-red-600 text-center mb-4">{error}</p>
                    <button
                      onClick={handleRetry}
                      className="px-4 py-2 rounded-lg text-white flex items-center gap-2"
                      style={{ backgroundColor: currentTheme.colors.primary }}
                    >
                      <RotateCcw className="w-4 h-4" />
                      Tekrar Dene
                    </button>
                  </>
                ) : (
                  <>
                    <div className="flex items-center space-x-3">
                      <div className="w-3 h-3 rounded-full animate-bounce" style={{ backgroundColor: currentTheme.colors.primary }} />
                      <div
                        className="w-3 h-3 rounded-full animate-bounce"
                        style={{ backgroundColor: currentTheme.colors.secondary, animationDelay: "0.1s" }}
                      />
                      <div
                        className="w-3 h-3 rounded-full animate-bounce"
                        style={{ backgroundColor: currentTheme.colors.accent, animationDelay: "0.2s" }}
                      />
                    </div>
                    <p className="mt-4 text-sm text-gray-600">AI öğretmen hazırlanıyor...</p>
                  </>
                )}
              </div>
            )}

            {step === "topicIntro" && mode === "topic" && topicData && (
              <div className="flex flex-col items-center justify-center min-h-[50vh] md:min-h-[70vh] px-3 sm:px-6 py-4 sm:py-8 max-w-4xl mx-auto w-full">

                <div
                  className="w-full rounded-2xl md:rounded-3xl p-4 sm:p-6 md:p-8 mb-4 sm:mb-6 md:mb-8 text-center shadow-lg md:shadow-2xl border"
                  style={{
                    background: `linear-gradient(135deg, ${currentTheme.colors.primary}15, ${currentTheme.colors.secondary}10, ${currentTheme.colors.accent}08)`,
                    borderColor: `${currentTheme.colors.primary}25`,
                  }}
                >
                  <div
                    className="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-xl md:rounded-2xl mx-auto mb-3 sm:mb-4 md:mb-6 flex items-center justify-center shadow-md md:shadow-lg"
                    style={{
                      background: `linear-gradient(135deg, ${currentTheme.colors.primary}, ${currentTheme.colors.secondary})`
                    }}
                  >
                    <Target className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 text-white" />
                  </div>

                  <h2
                    className="text-lg sm:text-xl md:text-3xl font-bold mb-2 sm:mb-3 px-2"
                    style={{
                      background: `linear-gradient(to right, ${currentTheme.colors.primary}, ${currentTheme.colors.accent})`,
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                    }}
                  >
                    {topicData.contentTitle}
                  </h2>

                  <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 mt-3 sm:mt-4">
                    {topicData.lesson && (
                      <span className="inline-flex items-center gap-1.5 sm:gap-2 bg-blue-100 text-blue-800 font-medium px-2.5 py-1.5 sm:px-4 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm">
                        <BookOpen className="w-3 h-3 sm:w-4 sm:h-4" />
                        {topicData.lesson.title}
                      </span>
                    )}
                    {topicData.topic && (
                      <span className="inline-flex items-center gap-1.5 sm:gap-2 bg-purple-100 text-purple-800 font-medium px-2.5 py-1.5 sm:px-4 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm">
                        <Sparkles className="w-3 h-3 sm:w-4 sm:h-4" />
                        {topicData.topic.title}
                      </span>
                    )}
                  </div>
                </div>

                <div className="w-full grid grid-cols-3 gap-2 sm:gap-4 md:gap-6 mb-4 sm:mb-6 md:mb-8">

                  <div className="bg-white rounded-xl md:rounded-2xl p-3 sm:p-4 md:p-6 border-2 border-amber-200 shadow-md md:shadow-lg hover:shadow-xl transition-all">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-14 md:h-14 rounded-lg md:rounded-xl bg-amber-100 flex items-center justify-center mb-2 sm:mb-3 md:mb-4 mx-auto md:mx-0">
                      <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 md:w-7 md:h-7 text-amber-600" />
                    </div>
                    <h3 className="text-xs sm:text-sm md:text-lg font-bold text-gray-800 mb-1 md:mb-2 text-center md:text-left">Eksik Kazanımlar</h3>
                    <p className="text-xl sm:text-2xl md:text-3xl font-bold text-amber-600 mb-1 md:mb-2 text-center md:text-left">
                      {topicData.gains.filter(g => !g.isResolved).length}
                    </p>
                    <p className="text-[10px] sm:text-xs md:text-sm text-gray-500 text-center md:text-left hidden sm:block">
                      Bu konuda giderilecek eksik kazanım bulunuyor
                    </p>
                  </div>

                  <div className="bg-white rounded-xl md:rounded-2xl p-3 sm:p-4 md:p-6 border-2 border-orange-200 shadow-md md:shadow-lg hover:shadow-xl transition-all">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-14 md:h-14 rounded-lg md:rounded-xl bg-orange-100 flex items-center justify-center mb-2 sm:mb-3 md:mb-4 mx-auto md:mx-0">
                      <Video className="w-4 h-4 sm:w-5 sm:h-5 md:w-7 md:h-7 text-orange-600" />
                    </div>
                    <h3 className="text-xs sm:text-sm md:text-lg font-bold text-gray-800 mb-1 md:mb-2 text-center md:text-left">Video İçerik</h3>
                    <p className="text-xl sm:text-2xl md:text-3xl font-bold text-orange-600 mb-1 md:mb-2 text-center md:text-left">
                      {topicData.videos.length}
                    </p>
                    <p className="text-[10px] sm:text-xs md:text-sm text-gray-500 text-center md:text-left hidden sm:block">
                      {topicData.videos.length > 0
                        ? "İlgili video bölümleri izlenecek"
                        : "Bu konu için video bulunmuyor"}
                    </p>
                  </div>

                  <div className="bg-white rounded-xl md:rounded-2xl p-3 sm:p-4 md:p-6 border-2 border-green-200 shadow-md md:shadow-lg hover:shadow-xl transition-all">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-14 md:h-14 rounded-lg md:rounded-xl bg-green-100 flex items-center justify-center mb-2 sm:mb-3 md:mb-4 mx-auto md:mx-0">
                      <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 md:w-7 md:h-7 text-green-600" />
                    </div>
                    <h3 className="text-xs sm:text-sm md:text-lg font-bold text-gray-800 mb-1 md:mb-2 text-center md:text-left">Pekiştirme Testi</h3>
                    <p className="text-xl sm:text-2xl md:text-3xl font-bold text-green-600 mb-1 md:mb-2 text-center md:text-left">10</p>
                    <p className="text-[10px] sm:text-xs md:text-sm text-gray-500 text-center md:text-left hidden sm:block">
                      Soru çözerek konuyu pekiştireceksin
                    </p>
                  </div>
                </div>

                <div className="w-full bg-gray-50 rounded-xl md:rounded-2xl p-3 sm:p-4 md:p-6 mb-4 sm:mb-6 md:mb-8">
                  <h3 className="text-sm sm:text-base md:text-lg font-semibold text-gray-800 mb-2 sm:mb-3 md:mb-4 text-center">Nasıl İlerleyecek?</h3>
                  <div className="flex flex-row items-center justify-center gap-1 sm:gap-2 md:gap-4 flex-wrap">
                    {topicData.videos.length > 0 && (
                      <>
                        <div className="flex items-center gap-1.5 sm:gap-2 md:gap-3 bg-white rounded-lg md:rounded-xl px-2 sm:px-3 md:px-5 py-1.5 sm:py-2 md:py-3 shadow-sm">
                          <div className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 rounded-full bg-orange-500 text-white flex items-center justify-center font-bold text-[10px] sm:text-xs md:text-sm">1</div>
                          <span className="font-medium text-gray-700 text-[10px] sm:text-xs md:text-sm">Video İzle</span>
                        </div>
                        <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 text-gray-400" />
                      </>
                    )}
                    <div className="flex items-center gap-1.5 sm:gap-2 md:gap-3 bg-white rounded-lg md:rounded-xl px-2 sm:px-3 md:px-5 py-1.5 sm:py-2 md:py-3 shadow-sm">
                      <div
                        className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 rounded-full text-white flex items-center justify-center font-bold text-[10px] sm:text-xs md:text-sm"
                        style={{ backgroundColor: currentTheme.colors.primary }}
                      >
                        {topicData.videos.length > 0 ? "2" : "1"}
                      </div>
                      <span className="font-medium text-gray-700 text-[10px] sm:text-xs md:text-sm">Soruları Çöz</span>
                    </div>
                    <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 text-gray-400" />
                    <div className="flex items-center gap-1.5 sm:gap-2 md:gap-3 bg-white rounded-lg md:rounded-xl px-2 sm:px-3 md:px-5 py-1.5 sm:py-2 md:py-3 shadow-sm">
                      <div className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 rounded-full bg-green-500 text-white flex items-center justify-center font-bold text-[10px] sm:text-xs md:text-sm">
                        {topicData.videos.length > 0 ? "3" : "2"}
                      </div>
                      <span className="font-medium text-gray-700 text-[10px] sm:text-xs md:text-sm">Tamamla</span>
                    </div>
                  </div>
                </div>

                <button
                  onClick={startTopicSession}
                  disabled={isSessionLoading}
                  className="px-6 sm:px-8 md:px-10 py-3 sm:py-3.5 md:py-4 rounded-xl md:rounded-2xl text-white font-bold text-sm sm:text-base md:text-lg flex items-center gap-2 sm:gap-3 shadow-lg md:shadow-xl hover:shadow-2xl transition-all hover:scale-105 disabled:opacity-70 disabled:hover:scale-100"
                  style={{
                    background: `linear-gradient(135deg, ${currentTheme.colors.primary}, ${currentTheme.colors.secondary})`
                  }}
                >
                  {isSessionLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 animate-spin" />
                      Hazırlanıyor...
                    </>
                  ) : (
                    <>
                      <Play className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" />
                      Başla
                    </>
                  )}
                </button>

                {error && (
                  <div className="mt-6 bg-red-50 border border-red-200 rounded-xl p-4 max-w-md">
                    <div className="flex items-center gap-2 text-red-600">
                      <AlertCircle className="w-5 h-5" />
                      <span className="text-sm font-medium">{error}</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            
            {step === "intro" && gainInfo && (
              <div className={`space-y-4 ${isExpanded ? "max-w-3xl w-full mx-auto" : ""}`}>
                <div
                  className={`rounded-2xl p-5 text-center shadow-lg border border-gray-200 ${isExpanded ? "p-8" : ""}`}
                  style={{
                    background: `linear-gradient(135deg, ${currentTheme.colors.primary}15, ${currentTheme.colors.secondary}10, ${currentTheme.colors.accent}15)`,
                    borderColor: `${currentTheme.colors.primary}30`,
                  }}
                >
                  <h3
                    className={`font-semibold mb-3 ${isExpanded ? "text-2xl mb-4" : "text-lg"}`}
                    style={{
                      background: `linear-gradient(to right, ${currentTheme.colors.primary}, ${currentTheme.colors.accent})`,
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                    }}
                  >
                    Şimdi Bu Konuya Odaklanalım:
                  </h3>
                  <p
                    className={`font-medium leading-relaxed px-3 py-2 rounded-lg inline-block ${isExpanded ? "text-base px-4 py-3" : "text-sm"}`}
                    style={{
                      color: currentTheme.colors.primary,
                      backgroundColor: `${currentTheme.colors.primary}10`
                    }}
                  >
                    "{gainInfo.gainText}"
                  </p>
                </div>

                {gainInfo.video && (
                  <div className={`bg-white rounded-xl border border-gray-200 shadow-sm ${isExpanded ? "p-6" : "p-4"}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <Video className={`${isExpanded ? "w-5 h-5" : "w-4 h-4"}`} style={{ color: currentTheme.colors.primary }} />
                      <span className={`font-medium text-gray-900 ${isExpanded ? "text-base" : "text-sm"}`}>Önerilen Video</span>
                    </div>

                    <h4 className={`font-semibold text-gray-900 mb-2 ${isExpanded ? "text-lg" : "text-sm"}`}>{gainInfo.video.title}</h4>

                    {getVimeoThumbnailUrl(gainInfo.video.url) && (
                      <div
                        className="relative rounded-lg overflow-hidden mb-3 cursor-pointer group"
                        onClick={handleWatchVideo}
                      >
                        <img
                          src={getVimeoThumbnailUrl(gainInfo.video.url)!}
                          alt={gainInfo.video.title}
                          className={`w-full object-cover transition-transform group-hover:scale-105 ${isExpanded ? "h-80 aspect-video" : "h-32"}`}
                        />
                        <div className="absolute inset-0 bg-black/30 flex items-center justify-center group-hover:bg-black/40 transition-colors">
                          <div
                            className="w-12 h-12 rounded-full flex items-center justify-center shadow-lg"
                            style={{ backgroundColor: currentTheme.colors.primary }}
                          >
                            <Play className="w-5 h-5 text-white ml-0.5" />
                          </div>
                        </div>
                        <div
                          className="absolute bottom-2 right-2 text-xs px-2 py-1 rounded text-white font-medium"
                          style={{ backgroundColor: currentTheme.colors.primary }}
                        >
                          {gainInfo.video.startTime} - {gainInfo.video.endTime}
                        </div>
                      </div>
                    )}

                    <p className={`text-gray-600 leading-relaxed ${isExpanded ? "text-base" : "text-sm"}`}>
                      Bu konudaki eksiğini kapatmak için videoyu izleyebilirsin. Ardından birkaç pekiştirme sorusuyla öğrendiklerini test edeceğiz.
                    </p>
                  </div>
                )}

                {!gainInfo.video && (
                  <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                    <p className="text-sm text-gray-600 text-center">
                      Bu konuyu pekiştirmek için sana birkaç soru hazırladım. Hazır olduğunda başlayalım!
                    </p>
                  </div>
                )}

                <div className="flex flex-col gap-2">
                  {gainInfo.video && (
                    <button
                      onClick={handleWatchVideo}
                      className="w-full p-3 rounded-xl text-white font-medium flex items-center justify-center gap-2 shadow-lg transition-all hover:opacity-90"
                      style={{ background: `linear-gradient(to right, ${currentTheme.colors.primary}, ${currentTheme.colors.secondary})` }}
                    >
                      <Play className="w-4 h-4" />
                      Video İzleyerek Başla
                    </button>
                  )}
                  <button
                    onClick={handleSkipVideo}
                    disabled={isSessionLoading}
                    className="w-full p-3 rounded-xl font-medium flex items-center justify-center gap-2 transition-all border border-gray-200 hover:bg-gray-50 text-gray-700 disabled:opacity-50"
                  >
                    {isSessionLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Sorular Hazırlanıyor...
                      </>
                    ) : (
                      <>
                        <ArrowRight className="w-4 h-4" />
                        {gainInfo.video ? "Video İzlemeden Başla" : "Başla"}
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {step === "video" && ((mode === "single" && gainInfo?.video) || (mode === "topic" && topicData && topicData.videos.length > 0)) && (
              <div className={`flex flex-col ${isExpanded ? "h-full max-w-5xl mx-auto w-full" : "space-y-4"}`}>

                <div
                  className={`rounded-xl border shadow-sm flex-shrink-0 ${isExpanded ? "p-5 mb-4" : "p-4"}`}
                  style={{
                    background: `linear-gradient(135deg, ${currentTheme.colors.primary}08, ${currentTheme.colors.secondary}05)`,
                    borderColor: `${currentTheme.colors.primary}20`,
                  }}
                >
                  {(() => {
                    const currentVideo = mode === "topic" && topicData
                      ? topicData.videos[currentVideoIndex]
                      : gainInfo?.video;
                    const videoTitle = currentVideo?.title || "";
                    const startTime = currentVideo?.startTime || "";
                    const endTime = currentVideo?.endTime || "";
                    const displayText = mode === "topic" && topicData ? topicData.contentTitle : gainInfo?.gainText;

                    return isExpanded ? (
                      <div className="flex flex-row items-center justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <Video className="w-5 h-5 flex-shrink-0" style={{ color: currentTheme.colors.primary }} />
                            <span className="font-semibold text-gray-900 text-base truncate">{videoTitle}</span>
                            {mode === "topic" && topicData && topicData.videos.length > 1 && (
                              <span className="text-sm text-gray-500">
                                ({currentVideoIndex + 1}/{topicData.videos.length})
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 line-clamp-2">
                            <span className="font-medium" style={{ color: currentTheme.colors.primary }}>
                              {mode === "topic" ? "Konu:" : "Kazanım:"}
                            </span>{" "}
                            {displayText}
                          </p>
                        </div>
                        <div
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium flex-shrink-0"
                          style={{
                            backgroundColor: `${currentTheme.colors.primary}15`,
                            color: currentTheme.colors.primary
                          }}
                        >
                          <span>{startTime}</span>
                          <span className="text-gray-400">→</span>
                          <span>{endTime}</span>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <Video className="w-4 h-4 flex-shrink-0" style={{ color: currentTheme.colors.primary }} />
                          <span className="font-semibold text-gray-900 text-sm">{videoTitle}</span>
                          {mode === "topic" && topicData && topicData.videos.length > 1 && (
                            <span className="text-xs text-gray-500">
                              ({currentVideoIndex + 1}/{topicData.videos.length})
                            </span>
                          )}
                        </div>

                        <p className="text-xs text-gray-600">
                          <span className="font-medium" style={{ color: currentTheme.colors.primary }}>
                            {mode === "topic" ? "Konu:" : "Kazanım:"}
                          </span>{" "}
                          {displayText}
                        </p>

                        <div
                          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium"
                          style={{
                            backgroundColor: `${currentTheme.colors.primary}15`,
                            color: currentTheme.colors.primary
                          }}
                        >
                          <span>{startTime}</span>
                          <span className="text-gray-400">→</span>
                          <span>{endTime}</span>
                        </div>
                      </div>
                    );
                  })()}
                </div>

                <div className={`rounded-xl overflow-hidden ${isExpanded ? "flex-1 min-h-[55vh]" : "aspect-video"}`}>
                  {(() => {
                    const currentVideo = mode === "topic" && topicData
                      ? topicData.videos[currentVideoIndex]
                      : gainInfo?.video;
                    if (!currentVideo) return null;
                    return (
                      <iframe
                        src={getVimeoEmbedUrl(currentVideo.url, currentVideo.startTime)}
                        className="w-full h-full"
                        allow="autoplay; fullscreen; picture-in-picture"
                        allowFullScreen
                        title={currentVideo.title}
                      />
                    );
                  })()}
                </div>

                <div className={`flex items-center justify-between pt-4 flex-shrink-0`}>
                  {mode === "topic" && topicData && topicData.videos.length > 1 ? (
                    <>
                      <button
                        onClick={handlePrevVideo}
                        disabled={currentVideoIndex === 0}
                        className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        ← Önceki Video
                      </button>

                      <button
                        onClick={handleSkipTopicVideos}
                        className="text-sm text-gray-500 hover:text-gray-700 underline"
                      >
                        Videoları Atla
                      </button>

                      <button
                        onClick={handleNextVideo}
                        className="px-6 py-2.5 rounded-xl text-white font-medium flex items-center gap-2 shadow-lg transition-all hover:opacity-90"
                        style={{ background: `linear-gradient(to right, ${currentTheme.colors.primary}, ${currentTheme.colors.secondary})` }}
                      >
                        {currentVideoIndex < topicData.videos.length - 1 ? (
                          <>
                            Sonraki Video
                            <ArrowRight className="w-4 h-4" />
                          </>
                        ) : (
                          <>
                            Sorulara Geç
                            <ArrowRight className="w-4 h-4" />
                          </>
                        )}
                      </button>
                    </>
                  ) : (
                    <div className="flex justify-end w-full">
                      <button
                        onClick={handleVideoComplete}
                        disabled={isSessionLoading && !session}
                        className="px-6 py-2.5 rounded-xl text-white font-medium flex items-center gap-2 shadow-lg transition-all hover:opacity-90 disabled:opacity-70"
                        style={{ background: `linear-gradient(to right, ${currentTheme.colors.primary}, ${currentTheme.colors.secondary})` }}
                      >
                        {isSessionLoading && !session ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Sorular Hazırlanıyor...
                          </>
                        ) : (
                          <>
                            <ArrowRight className="w-4 h-4" />
                            Devam Et
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {step === "explanation" && !session && isSessionLoading && (
              <div className={`flex flex-col items-center justify-center py-12 ${isExpanded ? "max-w-4xl w-full" : ""}`}>
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 rounded-full animate-bounce" style={{ backgroundColor: currentTheme.colors.primary }} />
                  <div
                    className="w-3 h-3 rounded-full animate-bounce"
                    style={{ backgroundColor: currentTheme.colors.secondary, animationDelay: "0.1s" }}
                  />
                  <div
                    className="w-3 h-3 rounded-full animate-bounce"
                    style={{ backgroundColor: currentTheme.colors.accent, animationDelay: "0.2s" }}
                  />
                </div>
                <p className="mt-4 text-sm text-gray-600">Sorular Hazırlanıyor...</p>
              </div>
            )}

            {step === "explanation" && session && (
              <div className={`space-y-4 ${isExpanded ? "max-w-4xl w-full" : ""}`}>
                {lessonFlow.length > 0 && (
                  <div className={`bg-white rounded-xl border border-gray-200 shadow-sm ${isExpanded ? "p-5" : "p-4"}`}>
                    <div className="flex items-center gap-2 mb-4">
                      <MessagesSquare className={`${isExpanded ? "w-5 h-5" : "w-4 h-4"}`} style={{ color: currentTheme.colors.primary }} />
                      <h3 className={`font-semibold text-gray-900 ${isExpanded ? "text-base" : "text-sm"}`}>Ders Akışı</h3>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {lessonFlow.map((item, index) => (
                        <div key={`${item}-${index}`} className="flex items-center gap-2 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
                          <span
                            className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                            style={{ backgroundColor: currentTheme.colors.primary }}
                          >
                            {index + 1}
                          </span>
                          <span className={`text-gray-700 leading-snug ${isExpanded ? "text-sm" : "text-xs"}`}>{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div
                  className={`rounded-xl border shadow-sm ${isExpanded ? "p-6" : "p-5"}`}
                  style={{
                    background: `linear-gradient(135deg, ${currentTheme.colors.primary}10, ${currentTheme.colors.secondary}05)`,
                    borderColor: `${currentTheme.colors.primary}20`,
                  }}
                >
                  <h3 className={`font-semibold text-gray-900 mb-3 flex items-center gap-2 ${isExpanded ? "text-base" : "text-sm"}`}>
                    <BookOpen className={`${isExpanded ? "w-5 h-5" : "w-4 h-4"}`} style={{ color: currentTheme.colors.primary }} />
                    Konu Açıklaması
                  </h3>
                  <p className={`text-gray-700 leading-relaxed ${isExpanded ? "text-base" : "text-sm"}`}>{session.explanation}</p>
                </div>

                {transcriptHighlights.length > 0 && (
                  <div className={`bg-slate-50 rounded-xl border border-slate-200 ${isExpanded ? "p-5" : "p-4"}`}>
                    <h3 className={`font-semibold text-gray-900 mb-3 flex items-center gap-2 ${isExpanded ? "text-base" : "text-sm"}`}>
                      <Video className={`${isExpanded ? "w-5 h-5" : "w-4 h-4"} text-slate-600`} />
                      Videodan Dayanak
                    </h3>
                    <div className="space-y-2">
                      {transcriptHighlights.slice(0, 4).map((item, index) => (
                        <div key={`${item.startTime || index}-${item.endTime || index}`} className="rounded-lg bg-white border border-slate-100 px-3 py-2">
                          <div className="text-[11px] font-semibold text-slate-500 mb-1">
                            {item.startTime || "00:00:00"} - {item.endTime || "00:00:00"}
                          </div>
                          <p className={`text-slate-700 leading-relaxed ${isExpanded ? "text-sm" : "text-xs"}`}>{item.text}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {session.examples.length > 0 && (
                  <div className={`bg-green-50 rounded-xl border border-green-100 ${isExpanded ? "p-6" : "p-5"}`}>
                    <h3 className={`font-semibold text-gray-900 mb-3 flex items-center gap-2 ${isExpanded ? "text-base" : "text-sm"}`}>
                      <Lightbulb className={`${isExpanded ? "w-5 h-5" : "w-4 h-4"} text-green-600`} />
                      Örnekler
                    </h3>
                    <ul className="space-y-2">
                      {session.examples.map((example, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <span className={`bg-green-500 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0 ${isExpanded ? "w-6 h-6 text-sm" : "w-5 h-5 text-xs"}`}>
                            {index + 1}
                          </span>
                          <span className={`text-gray-700 ${isExpanded ? "text-base" : "text-sm"}`}>{example}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <button
                  onClick={handleContinueFromExplanation}
                  className={`w-full rounded-xl text-white font-medium flex items-center justify-center gap-2 shadow-lg transition-all hover:opacity-90 ${isExpanded ? "p-4 text-base" : "p-3"}`}
                  style={{ background: `linear-gradient(to right, ${currentTheme.colors.primary}, ${currentTheme.colors.secondary})` }}
                >
                  {teacherDialogue.length > 0 ? (
                    <>
                      <MessagesSquare className={`${isExpanded ? "w-5 h-5" : "w-4 h-4"}`} />
                      Öğretmenle Devam Et
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className={`${isExpanded ? "w-5 h-5" : "w-4 h-4"}`} />
                      Kendini Test Et
                    </>
                  )}
                </button>
              </div>
            )}

            {step === "dialogue" && session && (
              <div className={`space-y-4 ${isExpanded ? "max-w-4xl w-full" : ""}`}>
                <div className={`bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden ${isExpanded ? "p-5" : "p-4"}`}>
                  <div className="flex items-center justify-between gap-3 mb-4">
                    <h3 className={`font-semibold text-gray-900 flex items-center gap-2 ${isExpanded ? "text-base" : "text-sm"}`}>
                      <Bot className={`${isExpanded ? "w-5 h-5" : "w-4 h-4"}`} style={{ color: currentTheme.colors.primary }} />
                      Öğretmenle Konu Anlatımı
                    </h3>
                    {session.video?.startTime && session.video?.endTime && (
                      <span className="text-xs font-medium text-gray-500 bg-gray-100 rounded-lg px-2.5 py-1">
                        {session.video.startTime} - {session.video.endTime}
                      </span>
                    )}
                  </div>

                  <div className="space-y-3 min-h-[280px]">
                    {teacherDialogue.slice(0, visibleDialogueCount).map((message, index) => {
                      const isTeacher = message.speaker !== "student";

                      return (
                        <div key={`${message.speaker}-${index}`} className={`tutor-message-in flex gap-3 ${isTeacher ? "justify-start" : "justify-end"}`}>
                          {isTeacher && (
                            <div
                              className={`rounded-full flex items-center justify-center text-white flex-shrink-0 ${isExpanded ? "w-9 h-9" : "w-8 h-8"}`}
                              style={{ background: `linear-gradient(135deg, ${currentTheme.colors.primary}, ${currentTheme.colors.secondary})` }}
                            >
                              <Bot className={`${isExpanded ? "w-5 h-5" : "w-4 h-4"}`} />
                            </div>
                          )}
                          <div
                            className={`rounded-2xl border leading-relaxed ${
                              isExpanded ? "max-w-[82%] px-4 py-3 text-sm" : "max-w-[86%] px-3 py-2.5 text-xs"
                            } ${
                              isTeacher
                                ? "bg-gray-50 border-gray-200 text-gray-800 rounded-tl-md"
                                : "border-transparent text-white rounded-tr-md"
                            }`}
                            style={!isTeacher ? { background: `linear-gradient(135deg, ${currentTheme.colors.primary}, ${currentTheme.colors.accent})` } : undefined}
                          >
                            <div className="flex items-center gap-1.5 mb-1">
                              {isTeacher ? <BookOpen className="w-3.5 h-3.5" /> : <UserRound className="w-3.5 h-3.5" />}
                              <span className={`font-semibold ${isTeacher ? "text-gray-700" : "text-white"}`}>
                                {isTeacher ? "Öğretmen" : "Öğrenci"}
                              </span>
                            </div>
                            <p>{message.text}</p>
                          </div>
                          {!isTeacher && (
                            <div
                              className={`rounded-full flex items-center justify-center text-white flex-shrink-0 ${isExpanded ? "w-9 h-9" : "w-8 h-8"}`}
                              style={{ backgroundColor: currentTheme.colors.accent }}
                            >
                              <UserRound className={`${isExpanded ? "w-5 h-5" : "w-4 h-4"}`} />
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {visibleDialogueCount < teacherDialogue.length && (
                      <div className="flex gap-3 justify-start">
                        <div
                          className={`rounded-full flex items-center justify-center text-white flex-shrink-0 ${isExpanded ? "w-9 h-9" : "w-8 h-8"}`}
                          style={{ background: `linear-gradient(135deg, ${currentTheme.colors.primary}, ${currentTheme.colors.secondary})` }}
                        >
                          <Bot className={`${isExpanded ? "w-5 h-5" : "w-4 h-4"}`} />
                        </div>
                        <div className="rounded-2xl rounded-tl-md border border-gray-200 bg-gray-50 px-4 py-3">
                          <div className="flex items-center gap-1.5">
                            <span className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: currentTheme.colors.primary }} />
                            <span className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: currentTheme.colors.secondary, animationDelay: "0.1s" }} />
                            <span className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: currentTheme.colors.accent, animationDelay: "0.2s" }} />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {visibleDialogueCount >= teacherDialogue.length && (
                  <div className="tutor-message-in space-y-4">
                    <div className={`rounded-xl border bg-blue-50 border-blue-100 ${isExpanded ? "p-5" : "p-4"}`}>
                      <div className="flex items-start gap-3">
                        <div className={`flex-shrink-0 ${isExpanded ? "w-10 h-10" : "w-8 h-8"} rounded-full bg-blue-500 flex items-center justify-center`}>
                          <CheckCircle2 className={`text-white ${isExpanded ? "w-5 h-5" : "w-4 h-4"}`} />
                        </div>
                        <div>
                          <h4 className={`font-semibold text-blue-900 mb-2 ${isExpanded ? "text-base" : "text-sm"}`}>Pekiştirme Testi</h4>
                          <p className={`text-blue-700 leading-relaxed ${isExpanded ? "text-sm" : "text-xs"}`}>
                            Öğrendiklerini test etmek için sana <strong>{session.questions.length} adet pekiştirme sorusu</strong> soracağım.
                            Her soru için 4 seçenek olacak ve doğru cevabı seçmen gerekecek.
                          </p>
                          <p className={`text-blue-700 leading-relaxed mt-2 ${isExpanded ? "text-sm" : "text-xs"}`}>
                            Bu sorulardan en az <strong>{Math.min(3, Math.ceil(session.questions.length * 0.6))} tanesini</strong> doğru yaparsan, bu konudaki eksiğin <strong>giderilmiş</strong> olarak işaretlenecek.
                          </p>
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={handleStartQuiz}
                      className={`w-full rounded-xl text-white font-medium flex items-center justify-center gap-2 shadow-lg transition-all hover:opacity-90 ${isExpanded ? "p-4 text-base" : "p-3"}`}
                      style={{ background: `linear-gradient(to right, ${currentTheme.colors.primary}, ${currentTheme.colors.secondary})` }}
                    >
                      <CheckCircle2 className={`${isExpanded ? "w-5 h-5" : "w-4 h-4"}`} />
                      Kendini Test Et
                    </button>
                  </div>
                )}
              </div>
            )}

            {step === "quiz" && session && currentQuestion && (
              <div className={`space-y-2 sm:space-y-3 pb-4 sm:pb-6 ${isExpanded || currentQuestion.mediaUrl ? "max-w-4xl w-full flex flex-col" : ""}`}>
                <div className={`flex items-center justify-between ${isExpanded ? "mb-2" : "mb-1"}`}>
                  <div className={`flex items-center flex-wrap ${isExpanded ? "gap-2 sm:gap-3" : "gap-1.5 sm:gap-2"}`}>
                    {session.questions.map((_, index) => (
                      <div
                        key={index}
                        className={`rounded-full flex items-center justify-center font-medium transition-colors cursor-pointer ${
                          isExpanded ? "w-8 h-8 sm:w-10 sm:h-10 md:w-11 md:h-11 text-sm sm:text-base" : "w-7 h-7 sm:w-9 sm:h-9 text-xs sm:text-sm"
                        } ${
                          index === currentQuestionIndex
                            ? "text-white"
                            : selectedAnswers[session.questions[index].id]
                            ? "bg-gray-600 text-white"
                            : "bg-gray-200 text-gray-600 hover:bg-gray-300"
                        }`}
                        style={index === currentQuestionIndex ? { backgroundColor: currentTheme.colors.primary } : {}}
                        onClick={() => setCurrentQuestionIndex(index)}
                      >
                        {index + 1}
                      </div>
                    ))}
                  </div>
                  <span className={`text-gray-500 font-medium ${isExpanded ? "text-sm sm:text-base" : "text-xs sm:text-sm"}`}>
                    {currentQuestionIndex + 1}/{session.questions.length}
                  </span>
                </div>

                <div
                  className={`bg-white rounded-xl sm:rounded-2xl border shadow-lg ${isExpanded ? "p-4 sm:p-6 md:p-8" : "p-3 sm:p-4 md:p-6"}`}
                  style={{
                    borderColor: `${currentTheme.colors.primary}20`,
                  }}
                >
                  {currentQuestion.mediaUrl && (
                    <div className="mb-3 sm:mb-4 rounded-lg overflow-hidden border border-gray-200 bg-gray-50 h-[220px] sm:h-[300px] md:h-[380px] lg:h-[420px]">
                      <img
                        src={currentQuestion.mediaUrl}
                        alt="Soru görseli"
                        className="w-full h-full object-contain"
                      />
                    </div>
                  )}
                  <p className={`font-semibold text-gray-900 mb-4 sm:mb-6 leading-relaxed ${isExpanded ? "text-base sm:text-lg md:text-xl" : "text-sm sm:text-base"}`}>
                    {currentQuestion.text}
                  </p>

                  {currentQuestion.mediaUrl ? (
                    <div className="flex gap-3 justify-center">
                      {Object.entries(currentQuestion.options).map(([key]) => (
                        <button
                          key={key}
                          onClick={() => handleAnswerSelect(currentQuestion.id, key)}
                          className={`flex-1 max-w-[100px] rounded-xl border-2 transition-all flex items-center justify-center ${
                            isExpanded ? "py-4" : "py-3"
                          } ${
                            selectedAnswers[currentQuestion.id] === key
                              ? "border-purple-500 bg-purple-50 shadow-md"
                              : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                          }`}
                        >
                          <span
                            className={`font-bold ${
                              isExpanded ? "text-lg" : "text-base"
                            } ${
                              selectedAnswers[currentQuestion.id] === key ? "text-purple-600" : "text-gray-700"
                            }`}
                          >
                            {key}
                          </span>
                        </button>
                      ))}
                    </div>
                  ) : (

                    <div className="flex flex-col gap-3">
                      {Object.entries(currentQuestion.options).map(([key, value]) => (
                        <button
                          key={key}
                          onClick={() => handleAnswerSelect(currentQuestion.id, key)}
                          className={`rounded-xl border-2 transition-all flex items-center text-left px-4 py-3 gap-3 ${
                            selectedAnswers[currentQuestion.id] === key
                              ? "border-purple-500 bg-purple-50 shadow-md"
                              : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                          }`}
                        >
                          <span
                            className={`font-bold flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                              selectedAnswers[currentQuestion.id] === key
                                ? "bg-purple-500 text-white"
                                : "bg-gray-100 text-gray-700"
                            } ${isExpanded ? "text-lg" : "text-base"}`}
                          >
                            {key}
                          </span>
                          <span className={`${isExpanded ? "text-base" : "text-sm"} ${
                            selectedAnswers[currentQuestion.id] === key ? "text-purple-700" : "text-gray-700"
                          }`}>
                            {value}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className={`flex items-center justify-between ${isExpanded ? "pt-4 sm:pt-6" : "pt-3 sm:pt-4"}`}>
                  <button
                    onClick={handlePrevQuestion}
                    disabled={currentQuestionIndex === 0}
                    className={`text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg sm:rounded-xl disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-all ${
                      isExpanded ? "px-3 sm:px-5 md:px-6 py-2 sm:py-2.5 md:py-3 text-sm sm:text-base md:text-lg" : "px-3 sm:px-4 md:px-5 py-2 sm:py-2.5 text-xs sm:text-sm md:text-base"
                    }`}
                  >
                    ← Önceki
                  </button>
                  {currentQuestionIndex < session.questions.length - 1 ? (
                    <button
                      onClick={handleNextQuestion}
                      className={`text-white rounded-lg sm:rounded-xl font-medium transition-all hover:opacity-90 shadow-lg ${
                        isExpanded ? "px-4 sm:px-6 md:px-8 py-2.5 sm:py-3 md:py-4 text-sm sm:text-base md:text-lg" : "px-3 sm:px-5 md:px-6 py-2 sm:py-2.5 md:py-3 text-xs sm:text-sm md:text-base"
                      }`}
                      style={{ backgroundColor: currentTheme.colors.primary }}
                    >
                      Sonraki →
                    </button>
                  ) : (
                    <button
                      onClick={handleSubmitQuiz}
                      disabled={!allAnswered || isSubmitting}
                      className={`bg-green-600 text-white rounded-lg sm:rounded-xl font-medium transition-all hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 sm:gap-2 shadow-lg ${
                        isExpanded ? "px-4 sm:px-6 md:px-8 py-2.5 sm:py-3 md:py-4 text-sm sm:text-base md:text-lg" : "px-3 sm:px-5 md:px-6 py-2 sm:py-2.5 md:py-3 text-xs sm:text-sm md:text-base"
                      }`}
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className={`animate-spin ${isExpanded ? "w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" : "w-4 h-4 sm:w-5 sm:h-5"}`} />
                          <span className="hidden sm:inline">Kontrol Ediliyor...</span>
                          <span className="sm:hidden">Kontrol...</span>
                        </>
                      ) : (
                        <>
                          <CheckCircle2 className={`${isExpanded ? "w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" : "w-4 h-4 sm:w-5 sm:h-5"}`} />
                          Gönder
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            )}

            {step === "result" && quizResult && session && (
              <div className={`space-y-4 ${isExpanded ? "max-w-2xl w-full" : ""}`}>
                {/* Üstte doğru/yanlış sayısı */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4 text-center">
                    <div className="flex items-center justify-center gap-2 mb-1">
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                      <span className="text-2xl font-bold text-green-700">{quizResult.correctCount}</span>
                    </div>
                    <span className="text-sm text-green-600 font-medium">Doğru</span>
                  </div>
                  <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 text-center">
                    <div className="flex items-center justify-center gap-2 mb-1">
                      <XCircle className="w-5 h-5 text-red-600" />
                      <span className="text-2xl font-bold text-red-700">{quizResult.totalQuestions - quizResult.correctCount}</span>
                    </div>
                    <span className="text-sm text-red-600 font-medium">Yanlış</span>
                  </div>
                </div>

                {/* Başarı mesajı */}
                <div
                  className={`rounded-2xl text-center ${isExpanded ? "p-6" : "p-4"} ${
                    quizResult.allCorrect ? "bg-green-50 border-2 border-green-200" : "bg-orange-50 border-2 border-orange-200"
                  }`}
                >
                  {quizResult.allCorrect ? (
                    <>
                      <div className={`rounded-full flex items-center justify-center mx-auto ${isExpanded ? "w-16 h-16 mb-4" : "w-12 h-12 mb-3"} bg-green-500`}>
                        <CheckCircle2 className={`text-white ${isExpanded ? "w-8 h-8" : "w-6 h-6"}`} />
                      </div>
                      <h3 className={`font-bold text-green-800 ${isExpanded ? "text-xl mb-2" : "text-lg mb-1"}`}>Tebrikler! 🎉</h3>
                      <p className={`text-green-700 font-medium ${isExpanded ? "text-base" : "text-sm"}`}>{quizResult.message}</p>
                    </>
                  ) : (
                    <>
                      <div className={`rounded-full flex items-center justify-center mx-auto ${isExpanded ? "w-16 h-16 mb-4" : "w-12 h-12 mb-3"} bg-orange-500`}>
                        <XCircle className={`text-white ${isExpanded ? "w-8 h-8" : "w-6 h-6"}`} />
                      </div>
                      <h3 className={`font-bold text-orange-800 ${isExpanded ? "text-xl mb-2" : "text-lg mb-1"}`}>
                        Biraz Daha Çalışmalısın
                      </h3>
                      <p className={`text-orange-700 font-medium ${isExpanded ? "text-base" : "text-sm"}`}>{quizResult.message}</p>
                    </>
                  )}
                </div>

                {/* Hatalarını İncele Butonu */}
                {!quizResult.allCorrect && (
                  <button
                    onClick={startReview}
                    className="w-full p-4 rounded-xl border-2 border-red-300 bg-red-50 text-red-700 font-semibold flex items-center justify-center gap-3 hover:bg-red-100 transition-all shadow-sm"
                  >
                    <AlertCircle className="w-5 h-5" />
                    Hatalarını İncele ({quizResult.totalQuestions - quizResult.correctCount} Soru)
                    <ArrowRight className="w-5 h-5" />
                  </button>
                )}

                {quizResult.allCorrect && remainingGains.length > 0 && (
                  <div
                    className={`rounded-xl border shadow-sm ${isExpanded ? "p-6" : "p-4"}`}
                    style={{
                      background: `linear-gradient(135deg, ${currentTheme.colors.primary}10, ${currentTheme.colors.accent}05)`,
                      borderColor: `${currentTheme.colors.primary}30`,
                    }}
                  >
                    <h4 className={`font-semibold text-gray-900 flex items-center gap-2 ${isExpanded ? "text-lg mb-4" : "text-sm mb-3"}`}>
                      <Target className={`${isExpanded ? "w-5 h-5" : "w-4 h-4"}`} style={{ color: currentTheme.colors.primary }} />
                      Şu Eksiklerini de Giderelim
                    </h4>
                    <div className={`${isExpanded ? "space-y-3" : "space-y-2"}`}>
                      {remainingGains.slice(0, 3).map((gain, index) => (
                        <button
                          key={gain.id}
                          onClick={() => selectGain(gain.id)}
                          className={`w-full rounded-xl border text-left transition-all flex items-start gap-3 hover:bg-white hover:shadow-md ${isExpanded ? "p-4" : "p-3"}`}
                          style={{ borderColor: `${currentTheme.colors.primary}20`, backgroundColor: `${currentTheme.colors.primary}05` }}
                        >
                          <span
                            className={`rounded-full flex items-center justify-center font-bold flex-shrink-0 text-white ${isExpanded ? "w-7 h-7 text-sm" : "w-5 h-5 text-xs"}`}
                            style={{ backgroundColor: currentTheme.colors.primary }}
                          >
                            {index + 1}
                          </span>
                          <span className={`text-gray-700 ${isExpanded ? "text-base" : "text-sm"}`}>{gain.gainText || gain.lessonSectionContentGain?.gain || `Kazanım ${index + 1}`}</span>
                        </button>
                      ))}
                    </div>
                    {remainingGains.length > 3 && (
                      <button
                        onClick={() => setStep("selection")}
                        className={`w-full text-center hover:underline cursor-pointer text-gray-500 hover:text-gray-700 transition-colors ${
                          isExpanded ? "text-sm mt-3" : "text-xs mt-2"
                        }`}
                      >
                        +{remainingGains.length - 3} Kazanım Daha Var
                      </button>
                    )}
                  </div>
                )}

                <div className="flex flex-col gap-2">
                  {!quizResult.allCorrect && (
                    <button
                      onClick={handleRetry}
                      className="w-full p-3 rounded-xl text-white font-medium flex items-center justify-center gap-2 shadow-lg transition-all hover:opacity-90"
                      style={{ backgroundColor: currentTheme.colors.primary }}
                    >
                      <RotateCcw className="w-4 h-4" />
                      Tekrar Dene
                    </button>
                  )}
                  <button
                    onClick={onClose}
                    className={`w-full p-3 rounded-xl font-medium flex items-center justify-center gap-2 transition-all ${
                      quizResult.allCorrect
                        ? "bg-green-600 text-white hover:bg-green-700"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200"
                    }`}
                  >
                    <X className="w-4 h-4" />
                    Kapat
                  </button>
                </div>
              </div>
            )}

            {/* Yanlış soruyu göster - SORU ADIMI */}
            {step === "reviewQuestion" && wrongQuestions.length > 0 && wrongQuestions[currentReviewIndex] && (
              <div className={`space-y-4 ${isExpanded ? "max-w-4xl w-full" : ""}`}>
                {/* Üst bar - ilerleme */}
                <div className="flex items-center justify-between bg-white rounded-xl p-3 border border-gray-200 shadow-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-600">Yanlış Soru:</span>
                    <span className="text-lg font-bold" style={{ color: currentTheme.colors.primary }}>
                      {currentReviewIndex + 1} / {wrongQuestions.length}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {wrongQuestions[currentReviewIndex].question.solutionVideoUrl && (
                      <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded-full font-medium flex items-center gap-1">
                        <Video className="w-3 h-3" />
                        Video Çözüm Var
                      </span>
                    )}
                  </div>
                </div>

                {/* Soru kartı */}
                <div className="bg-white rounded-2xl border-2 border-gray-200 shadow-lg overflow-hidden">
                  <div className="p-6">
                    {/* Soru numarası ve metin */}
                    <div className="flex items-start gap-4 mb-4">
                      <span className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold text-white bg-red-500">
                        {wrongQuestions[currentReviewIndex].index + 1}
                      </span>
                      <p className="font-medium text-gray-900 text-base leading-relaxed pt-1">
                        {wrongQuestions[currentReviewIndex].question.text}
                      </p>
                    </div>

                    {/* Soru görseli */}
                    {wrongQuestions[currentReviewIndex].question.mediaUrl && (
                      <div className="mb-6 rounded-xl overflow-hidden bg-gray-50 border border-gray-200 h-[220px] sm:h-[300px] md:h-[380px] lg:h-[420px]">
                        <img
                          src={wrongQuestions[currentReviewIndex].question.mediaUrl}
                          alt="Soru görseli"
                          className="w-full h-full object-contain cursor-pointer hover:opacity-95 transition-opacity"
                          onClick={() => setLightboxImage(wrongQuestions[currentReviewIndex].question.mediaUrl!)}
                        />
                      </div>
                    )}

                    {/* Cevaplar */}
                    <div className="space-y-3">
                      {Object.entries(wrongQuestions[currentReviewIndex].question.options).map(([key, value]) => {
                        const isUserAnswer = wrongQuestions[currentReviewIndex].result.userAnswer === key;
                        const isCorrectAnswer = wrongQuestions[currentReviewIndex].question.correctAnswer === key;
                        
                        let bgColor = "bg-gray-50 border-gray-200";
                        let textColor = "text-gray-700";
                        let badgeColor = "bg-gray-200 text-gray-700";
                        let icon = null;
                        
                        if (isUserAnswer && !isCorrectAnswer) {
                          bgColor = "bg-red-50 border-red-300";
                          textColor = "text-red-700";
                          badgeColor = "bg-red-500 text-white";
                          icon = <XCircle className="w-5 h-5 text-red-500" />;
                        } else if (isCorrectAnswer) {
                          bgColor = "bg-green-50 border-green-300";
                          textColor = "text-green-700";
                          badgeColor = "bg-green-500 text-white";
                          icon = <CheckCircle2 className="w-5 h-5 text-green-500" />;
                        }
                        
                        return (
                          <div
                            key={key}
                            className={`flex items-center gap-3 p-4 rounded-xl border-2 ${bgColor}`}
                          >
                            <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${badgeColor}`}>
                              {key}
                            </span>
                            <span className={`flex-1 font-medium ${textColor}`}>{value}</span>
                            {icon}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Navigasyon butonları */}
                <div className="flex items-center justify-between gap-3">
                  <button
                    onClick={handlePrevReviewStep}
                    className="px-4 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 font-medium text-sm"
                  >
                    ← Geri
                  </button>
                  
                  <button
                    onClick={handleNextReviewStep}
                    className="px-5 py-2.5 rounded-lg text-white font-medium flex items-center justify-center gap-2 shadow-md transition-all hover:opacity-90 text-sm"
                    style={{ background: `linear-gradient(to right, ${currentTheme.colors.primary}, ${currentTheme.colors.secondary})` }}
                  >
                    {wrongQuestions[currentReviewIndex].question.solutionVideoUrl ? (
                      <>
                        <Video className="w-4 h-4" />
                        Video Çözümü İzle
                      </>
                    ) : currentReviewIndex < wrongQuestions.length - 1 ? (
                      <>
                        Sonraki Soru
                        <ArrowRight className="w-4 h-4" />
                      </>
                    ) : (
                      <>
                        Tamamla
                        <CheckCircle2 className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Video çözüm adımı */}
            {step === "reviewVideo" && wrongQuestions.length > 0 && wrongQuestions[currentReviewIndex] && wrongQuestions[currentReviewIndex].question.solutionVideoUrl && (
              <div className={`flex flex-col ${isExpanded ? "h-full max-w-5xl mx-auto w-full" : "space-y-4"}`}>
                {/* Üst bar */}
                <div
                  className={`rounded-xl border shadow-sm flex-shrink-0 ${isExpanded ? "p-4 mb-4" : "p-3"}`}
                  style={{
                    background: `linear-gradient(135deg, ${currentTheme.colors.primary}08, ${currentTheme.colors.secondary}05)`,
                    borderColor: `${currentTheme.colors.primary}20`,
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                        <Video className="w-5 h-5 text-orange-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">Video Çözüm</p>
                        <p className="text-sm text-gray-600">Soru {wrongQuestions[currentReviewIndex].index + 1}</p>
                      </div>
                    </div>
                    <span className="text-sm font-medium px-3 py-1 rounded-full" style={{ backgroundColor: `${currentTheme.colors.primary}15`, color: currentTheme.colors.primary }}>
                      {currentReviewIndex + 1} / {wrongQuestions.length}
                    </span>
                  </div>
                </div>

                {/* Video */}
                <div className={`rounded-xl overflow-hidden ${isExpanded ? "flex-1 min-h-[55vh]" : "aspect-video"}`}>
                  <iframe
                    src={getVimeoEmbedUrl(wrongQuestions[currentReviewIndex].question.solutionVideoUrl!, "0:00")}
                    className="w-full h-full"
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen
                    title={`Soru ${wrongQuestions[currentReviewIndex].index + 1} Video Çözümü`}
                  />
                </div>

                {/* Navigasyon butonları */}
                <div className="flex items-center justify-between gap-3 pt-4">
                  <button
                    onClick={handlePrevReviewStep}
                    className="px-4 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 font-medium text-sm"
                  >
                    ← Soruya Dön
                  </button>
                  
                  <button
                    onClick={handleNextReviewStep}
                    className="px-5 py-2.5 rounded-lg text-white font-medium flex items-center justify-center gap-2 shadow-md transition-all hover:opacity-90 text-sm"
                    style={{ background: `linear-gradient(to right, ${currentTheme.colors.primary}, ${currentTheme.colors.secondary})` }}
                  >
                    {currentReviewIndex < wrongQuestions.length - 1 ? (
                      <>
                        Sonraki Soru
                        <ArrowRight className="w-4 h-4" />
                      </>
                    ) : (
                      <>
                        Tamamla
                        <CheckCircle2 className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Lightbox */}
            {lightboxImage && (
              <div
                className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm animate-in fade-in duration-200"
                onClick={() => setLightboxImage(null)}
              >
                <button
                  onClick={() => setLightboxImage(null)}
                  className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors z-10"
                >
                  <X className="w-6 h-6 text-white" />
                </button>
                <img
                  src={lightboxImage}
                  alt="Soru görseli - Büyütülmüş"
                  className="max-w-[95vw] max-h-[95vh] object-contain cursor-zoom-out"
                  onClick={(e) => {
                    e.stopPropagation();
                    setLightboxImage(null);
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default DeficiencyTutorPanel;
